import { Organization } from "common/Database";
import { dontAwait } from "common/Support";
import { getOrganizationPersistentInviteLink, getOrgMembers, OrgMember } from "designer/apps/orgs/orgs-lib";
import { LoadingIndicator } from "designer/apps/orgs/orgs-styles";
import sortBy from "lodash/sortBy";
import React, { useCallback, useEffect, useState } from "react";
import { styled } from "sharedUI/styles";
import { BUILDER_BUSY_CONTAINER } from "sharedUI/z-index";
import WindowedModalV2 from "../windowed-modal/windowed-modal";
import TeamInvite from "./team-invite";
import TeamInviteLink from "./team-invite-link";

interface TeamInviteProps {
    readonly organization: Organization;
    readonly onClose: () => void;
    readonly userID: string;
    readonly source: string;
}

const TeamInviteModalStyle = styled.div`
    padding: 24px 32px;
    width: 480px;
`;

const LoadingWrapper = styled.div`
    > div {
        position: fixed;
        z-index: ${BUILDER_BUSY_CONTAINER};
    }
`;

const TeamInviteModalImpl: React.FunctionComponent<TeamInviteProps> = p => {
    const { onClose, organization, userID, source } = p;

    const [orgMembers, setOrgMembers] = useState<readonly OrgMember[] | "loading">("loading");
    const [inviteLink, setInviteLink] = React.useState<string | undefined>(undefined);

    const fetchOrgMembers = useCallback(async () => {
        const newOrgMembers = await getOrgMembers(organization.id);
        if (newOrgMembers !== undefined) {
            const newSortedOrgMembers = sortBy(newOrgMembers, t => t.status).reverse();
            setOrgMembers(newSortedOrgMembers);
        }
    }, [organization.id]);

    const getInviteLink = React.useCallback(
        async (createNew: boolean = false) => {
            if (organization.id === undefined) return;

            const persistentInviteLink = await getOrganizationPersistentInviteLink(organization.id, createNew);
            setInviteLink(persistentInviteLink);
        },
        [organization.id]
    );

    useEffect(() => {
        dontAwait(fetchOrgMembers());
        dontAwait(getInviteLink());
    }, [organization.id, getInviteLink, fetchOrgMembers]);

    if (orgMembers === "loading" || inviteLink === undefined) {
        return (
            <LoadingWrapper>
                <LoadingIndicator />
            </LoadingWrapper>
        );
    }

    const footer = <TeamInviteLink orgID={organization.id} getInviteLink={getInviteLink} inviteLink={inviteLink} />;
    return (
        <WindowedModalV2 onClose={onClose} canClose={true} padding={0} floatingFooter={footer}>
            <TeamInviteModalStyle>
                <TeamInvite source={source} organization={organization} userID={userID} orgMembers={orgMembers} />
            </TeamInviteModalStyle>
        </WindowedModalV2>
    );
};

const TeamInviteModal = TeamInviteModalImpl;
export default TeamInviteModal;
